// The desktop rendering of "the whole row opens the thing" — the table half of
// the promise RowCard already makes on phones, where the entire card is the
// tap target rather than a small link inside one.
//
// The name link in the first cell STAYS where a page has one: it is the
// keyboard path, the open-in-new-tab path, and the visible signal that the row
// goes somewhere. This component only widens the pointer target to the rest of
// the row.
//
// Three refusals keep an ordinary table usable:
//   - A click on anything interactive inside the row (a link, a button, a
//     select, an ActionMenu) belongs to that control, never to the row.
//   - A modified click (ctrl/cmd/shift/alt) belongs to the browser — the real
//     link in the row is how "open in new tab" works, and the row must not
//     swallow it into a same-tab navigation.
//   - A click that ends a text selection is a copy, not a navigation. People
//     lift regos, job numbers and email addresses straight off these tables.
import { useNavigate } from 'react-router-dom';
import type { MouseEvent, ReactNode } from 'react';

export function LinkedRow({
  to,
  onOpen,
  children,
}: {
  /** Route the row navigates to. */
  to?: string;
  /** For rows that open in place (the document viewer) rather than routing. */
  onOpen?: () => void;
  children: ReactNode;
}) {
  const navigate = useNavigate();

  function click(event: MouseEvent<HTMLTableRowElement>) {
    const target = event.target as Element;
    if (target.closest('a, button, input, select, textarea, label')) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (window.getSelection()?.toString()) return;
    if (onOpen) onOpen();
    else if (to) navigate(to);
  }

  return (
    <tr className="cursor-pointer" onClick={click}>
      {children}
    </tr>
  );
}
